import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { DisplayModus, FORM_DISPLAY_MODUS, HTTP_CODES } from 'constants/constants'
import { useForm, SubmitHandler, FormProvider, SubmitErrorHandler } from 'react-hook-form'
import FormInput from 'components/FormFields/FormInput'
import FormDate from 'components/FormFields/FormDate'
import FormSelect from 'components/FormFields/FormSelect'
import urlApis from 'utils/api/urlApis'
import axiosUtils from 'utils/api/axiosUtils'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { parse, format } from 'date-fns'
import { DevTool } from '@hookform/devtools'
import axios from 'utils/api/axios'
import { Message, MessageSeverity } from 'constants/message'
import { Button, Col, ComboBoxDefaultOption, Flex, IconButton, Inline, Row } from '@airbus/components-react/components'
import { manageFormErrors } from 'components/FormFields/FormField'
import tools from 'utils/helpers'
import ModalPn from 'components/Modal/modalPn'
import ModalSn from 'components/Modal/modalSn'
import myLocalStorage from 'utils/myLocalStorage'
import { PnSearch, SnSearch } from 'types/pn'
import { Lang } from 'context/LangContext'
import useQuery from 'utils/hooks/useQuery'
import { WoIssuer } from 'types/workorder/woIssuer'

import WoExecutingForm from './components/woExecutionForm'
import WoWorkForm from './components/woWorkForm'
import FormControl from 'components/FormFields/FormControl'
import {
  approStore,
  execLocationSocMag,
  execLocationThirdParty,
  execStore,
  SubType,
  Workorder,
  WorkorderInput,
} from 'types/workorder/workorder'
import useMutation from 'utils/hooks/useMutation'
import MessageContext from 'context/MessageContext'
import URL_APIS from 'utils/api/urlApis'
import useOptions from 'utils/hooks/useOptions'
import LoadingContext from 'context/LoadingContext'
import throttle from 'lodash.throttle'
import ComponentsUNames from 'constants/componentsUNames'
import ModalConfirm from 'components/Modal/ModalConfim'
import { useNavigate } from 'react-router-dom'

const DEFAULT_OPTION = { label: '-', value: '' }

export const renderOptionLabel = (o: ComboBoxDefaultOption) => (o?.label ? `${o?.value} - ${o.label}` : o?.value)

type MyMelItemDetail = {
  applCode?: string
  melItem?: string
  melType?: string
  melLib?: string
  melProc?: string
  hilCode?: string
  etops?: boolean

  codeMelItem: string
  melLibFull: string
}

type Props = {
  lang: Lang
  oenumdoc: string | null
  woInit?: Workorder
  displayMode: DisplayModus
  setDisplayMode: (s: string) => void
  onSubmitWorkOrder: () => void
  onDetailsLoaded: (
    statusAbv: string,
    oeType: string,
    applicability: string,
    woApproStore: approStore,
    woExecStore: execStore,
  ) => void
  onReloadManHour: () => void
}

const WoDetails = ({
  lang,
  oenumdoc,
  woInit,
  displayMode,
  setDisplayMode,
  onSubmitWorkOrder,
  onDetailsLoaded,
  onReloadManHour,
}: Props) => {
  const { t } = useTranslation()
  const formTitle = t('9500144')
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress, isLoading } = useContext(LoadingContext)

  const navigate = useNavigate()

  const [data, setData] = useState<Workorder | undefined>(woInit)
  const [allSubTypes, setAllSubTypes] = useState<SubType[]>([])

  const { loadOptions: loadOptionsCompanies, options: companiesOptions } = useOptions(
    URL_APIS.URL_COMMON_LOCATION_LIST_COMPANIES_GET,
  )
  const [issuerStoreOptions, setIssuerStoreOptions] = useState([DEFAULT_OPTION])
  const [execLocStoreOptions, setExecLocStoreOptions] = useState([DEFAULT_OPTION])
  const [execLocServiceOptions, setExecLocServiceOptions] = useState([DEFAULT_OPTION])
  const { loadOptions: loadOptionsSubtypes, options: subtypesOptions } = useOptions(
    URL_APIS.URL_WORKORDER_SUBTYPES_MC_GET,
  )
  const { loadOptions: loadOptionsWorkStation, options: workStationOptions } = useOptions(
    URL_APIS.URL_COMMON_LIST_WORKSTATIONS_GET,
  )
  const { loadOptions: loadOptionsControls, options: controlOptions } = useOptions(
    URL_APIS.URL_COMMON_LIST_CONTROLCODES_GET,
  )
  const { loadOptions: loadOptionsTasks, options: taskOptions } = useOptions(URL_APIS.URL_COMMON_LIST_TASKCODES_GET)
  const { loadOptions: loadOptionsPhases, options: phaseOptions } = useOptions(URL_APIS.URL_COMMON_LIST_PHASES_GET)
  const { loadOptions: loadOptionsZones, options: zoneOptions } = useOptions(URL_APIS.URL_COMMON_LIST_ACZONES_GET)
  const { loadOptions: loadOptionsQualifications, options: qualificationsOptions } = useOptions<
    ComboBoxDefaultOption[]
  >(URL_APIS.URL_COMMON_QUALIFICATIONS)
  const { loadOptions: loadOptionsNoCompletionCodes, options: noCompletionCodeOptions } = useOptions(
    URL_APIS.URL_COMMON_NON_PERFORMANCE_CODES_GET,
  )
  const { loadOptions: loadOptionsMelCodes, options: melCodesOptions } = useOptions(URL_APIS.URL_COMMON_LIST_MEL_CODES)
  const { loadOptions: loadOptionsMelItems, options: melItemsOptions } = useOptions(URL_APIS.URL_COMMON_LIST_MEL_ITEMS)
  const { loadOptions: loadOptionsPersonnel, options: personnelOptions } = useOptions(
    URL_APIS.URL_COMMON_LIST_PERSONNEL,
  )

  const [modalPnOpened, setModalPnOpened] = useState(false)
  const [modalSnOpened, setModalSnOpened] = useState(false)
  const [isConfirmOpened, setIsConfirmOpened] = useState(false)

  const { fetchData: fetchWoIssuer } = useQuery<WoIssuer>(urlApis.URL_COMMON_CUSTOMISATION_WO_ISSUER_GET)
  const { fetchData: fetchSubTypes } = useQuery<SubType>(urlApis.URL_WORKORDER_SUBTYPES_MC_GET)

  const { fetchData: fetchWorkorder } = useQuery<Workorder>(urlApis.URL_WORKORDER_GET)
  const { mutate: createOrUpdateWorkorder } = useMutation<WorkorderInput>(urlApis.URL_WORKORDER_CREATE_POST)
  const { mutate: completeWorkorder } = useMutation<WorkorderInput>(urlApis.URL_WORKORDER_EXEC_COMPLETION_PUT)
  const { mutate: noCompleteWorkorder } = useMutation<WorkorderInput>(urlApis.URL_WORKORDER_EXEC_NO_COMPLETION_PUT)
  const { mutate: hilWorkorder } = useMutation<WorkorderInput>(urlApis.URL_WORKORDER_EXEC_HIL_PUT)

  const creating = displayMode === DisplayModus.CREATE
  const readOnly = displayMode === DisplayModus.READ
  const editing = displayMode === DisplayModus.WRITE
  const executing = displayMode === DisplayModus.EXEC
  const isWorkCodeMandatory = myLocalStorage.getOrDefault(
    [myLocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_WO_MANDATORY_WORKCODE],
    myLocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const loadWorkorder = async () => {
    const workorderResponse = await fetchWorkorder({
      lang: lang,
      oenumdoc: oenumdoc,
    })
    return workorderResponse
  }

  const loadData = useCallback(async () => {
    if (oenumdoc) {
      const response = await loadWorkorder()
      const responseData: Workorder = response?.data || {}
      if (responseData) {
        setData(responseData)

        const openingDate = responseData.openingDate
        const type = responseData?.type?.type

        if (responseData?.aircraftNumber) {
          loadOptionsWithAvnumap(responseData?.aircraftNumber)
        }

        onDetailsLoaded(
          responseData?.statusAbv,
          responseData?.type.code,
          responseData?.appl,
          {
            company: responseData?.approLocation?.companyCode,
            store: responseData?.approLocation?.storeCode,
          },
          {
            company: responseData?.execLocation?.companyCode,
            store: responseData?.execLocation?.storeCode,
            third: responseData?.execLocation?.thirdCode,
          },
        )

        const issuer = {
          companyCode: responseData?.issuer?.companyCode,
          storeCode: responseData?.issuer?.storeCode,
        }

        if (type) {
          loadOptionsSubtypes({ type: type }, undefined, undefined, 'code', false, false)
        }

        if (issuer.companyCode) {
          updateDependantOptions(issuer.companyCode, 'issuer', setIssuerStoreOptions)
        }

        const execLocation: execLocationSocMag | execLocationThirdParty = {
          companyCode:
            responseData?.execLocation?.companyCode ||
            myLocalStorage.getOrDefault([myLocalStorage.KEY_USER_COMPANY], myLocalStorage.VALUE_TYPE.STRING, undefined),
          storeCode:
            responseData?.execLocation?.storeCode ||
            myLocalStorage.getOrDefault([myLocalStorage.KEY_USER_STORE], myLocalStorage.VALUE_TYPE.STRING, undefined),
          serviceCode:
            responseData?.execLocation?.serviceCode ||
            myLocalStorage.getOrDefault([myLocalStorage.KEY_USER_SERVICE], myLocalStorage.VALUE_TYPE.STRING, undefined),
          thirdCode: responseData?.execLocation?.thirdCode,
        }

        if (execLocation.companyCode) {
          updateDependantOptions(execLocation.companyCode, 'execLocation', setExecLocStoreOptions)
          if (execLocation.storeCode) updateExecLocServiceOptions(execLocation.companyCode, execLocation.storeCode)
        }

        setData({
          ...responseData,
          aircraftNumber: responseData.aircraft || responseData.aircraftNumber,
          aircraftRegistration: responseData.avimmatr || responseData.aircraftRegistration,
          oenumdoc: responseData.no || oenumdoc || '',
          chantier: responseData.chantier || '',
          status: responseData.status || '',
          pn: responseData.pn || '',
          sn: responseData.sn || '',
          type: {
            type: responseData?.type?.type || '1',
            code: responseData?.type?.code || 'TL',
            label: responseData?.type?.label || '',
          },
          subtype: {
            type: responseData?.subtype?.type || '1',
            subType: responseData?.subtype?.subType || '',
            code: responseData?.subtype?.code || '',
            label: responseData?.subtype?.label || '',
            defaultCtrlCode: responseData?.subtype?.defaultCtrlCode || '',
          },
          openingDate: openingDate && parse(openingDate, 'P', new Date()),
          reference: { ...(responseData?.reference || {}) },
          formattedReference: (responseData?.reference || {})?.techlogNumber?.replace(/\s+/g, '  '),
          qualifications: responseData?.qualifications || [],
          issuer,
          execLocation,
          ata: responseData.ata || '',
          workStation: responseData.workStation || '',
          phase: responseData.phase || '',
          zone: responseData.zone || '',
          jobState: responseData.jobState || '',
          noCompletionCode: '',
          noCompletionReason: '',
          hilDate: new Date(),
          hilHeur: format(new Date(), 'HH:mm'),
          melItem: responseData.melItem?.replace('true', '').replace('false', ''),
          personnel: myLocalStorage.getOrDefault(
            [myLocalStorage.KEY_USER],
            myLocalStorage.VALUE_TYPE.STRING,
            undefined,
          ),
        })
      }
    }
  }, [oenumdoc, displayMode])

  const methods = useForm<WorkorderInput>({
    values: { ...data, ...woInit },
  })

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError,
    control,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = methods

  const woType = watch('type.type')
  const selectedExecLocationCompany = watch('execLocation.companyCode')
  const selectedExecLocationStore = watch('execLocation.storeCode')
  const selectedPn = watch('pn')

  const processEndpointMessages = (response: any) => {
    response?.messages?.map((response: any) => {
      switch (response?.amasisCode) {
        // case 'SOE1031':
        case 'GEN8211':
          // Date doit être renseignée (readonly)
          break
        case 'SOC1001':
          // Société inconnue
          setError('issuer.companyCode', { type: 'custom' })
          break
        case 'SOC1010':
          // Cette société ne fait pas partie du système
          setError('issuer.companyCode', { type: 'custom' })
          break
        case 'OLD8023':
          // AU MOINS UNE DE CES ZONES DOIT ETRE RENSEIGNEE
          break
        case 'OLD8260':
          // LE MAGASIN/CENTRE N'A PAS LE DROIT DE GÉNÉRER CE TYPE DE DOCUMENT

          break
        case 'OLD8035':
          // CETTE ZONE NE DOIT PAS ETRE MODIFIEE

          break
        case 'SRM4154':
          setError('reference.incidentNumber', { type: 'custom' })
          break
        default:
      }
    })
  }

  const onSubmit: SubmitHandler<WorkorderInput> = async (data) => {
    const response = null

    try {
      const requestData = {
        ...data,
        openingDate: data.openingDate && format(data.openingDate, 'P'),
        issuer: {
          companyCode: data.issuer.companyCode && data.issuer.companyCode.toUpperCase(),
          storeCode: data.issuer.storeCode && data.issuer.storeCode.toUpperCase(),
        },
        execLocation: data.execLocation.thirdCode ? { thirdCode: data.execLocation.thirdCode } : data.execLocation,
        hilDate: creating ? null : data.hilDate && format(data.hilDate, 'P'),
        hilHeur: creating ? null : data.hilHeur && format(parse(data.hilHeur, 'HH:mm', new Date()), 'HHmm'),
        melItem: data?.melItem?.replace('true', '').replace('false', ''),
        personnel: creating ? null : data.personnel,
        reference: { ...data.reference, incidentNumber: data.reference?.incidentNumber?.toUpperCase() },
        pn: data.pn && data.pn.toUpperCase(),
        sn: data.sn && data.sn.toUpperCase(),
        ata: data.ata && data.ata.toUpperCase(),
        subtype: {
          ...(subtypesOptions.find((so) => so.value === data.subtype?.code) || {}),
          defaultCtrlCode: data.subtype.defaultCtrlCode,
        },
        numdoc: data.oenumdoc,
      }

      switch (displayMode) {
        case DisplayModus.WRITE: {
          const response = await createOrUpdateWorkorder({
            data: requestData,
            params: { lang },
            method: 'PUT',
          })
          onSubmitWorkOrder(displayMode, oenumdoc)
          break
        }
        case DisplayModus.CREATE: {
          const response = await createOrUpdateWorkorder({
            data: requestData,
            params: { lang },
            method: 'POST',
          })
          onSubmitWorkOrder(displayMode, response?.data?.entityId)
          break
        }
        case DisplayModus.EXEC: {
          switch (data.jobState) {
            case 'done': {
              const response = await completeWorkorder({
                data: {},
                params: { lang: lang, oenumdoc: data.oenumdoc, performedWork: data.performedWork },
                method: 'PUT',
              })
              onSubmitWorkOrder(displayMode, oenumdoc)
              break
            }
            case 'not done': {
              const response = await noCompleteWorkorder({
                data: {},
                params: {
                  lang: lang,
                  oenumdoc: data.oenumdoc,
                  code: data.noCompletionCode,
                  reason: data.noCompletionReason,
                },
                method: 'PUT',
              })
              onSubmitWorkOrder(displayMode, oenumdoc)
              break
            }
            case 'HIL': {
              const response = await hilWorkorder({
                data: requestData,
                params: {
                  lang: lang,
                },
                method: 'PUT',
              })
              onSubmitWorkOrder(displayMode, oenumdoc)
              break
            }
            default:
              break
          }
          break
        }
        default:
          break
      }
    } catch (err: any) {
      processEndpointMessages(err.response.data)
    } finally {
      processEndpointMessages(response?.data)
    }
  }

  const onError: SubmitErrorHandler<WorkorderInput> = (errors) => {
    console.debug({ errors })
    manageFormErrors(errors, (s: string, t: string) => handleDisplayMessage(new Message(s, '', null, t)))
  }

  const loadWoIssuer = async () => {
    // Get SOC/MAG for WO-Issuer
    const woIssuerResponse = await fetchWoIssuer()
    updateDependantOptions(woIssuerResponse?.data?.companyCode, 'issuer', setIssuerStoreOptions)
    setValue('issuer', woIssuerResponse?.data)
  }

  const loadSubTypes = async () => {
    const woSubTypesResponse = await fetchSubTypes({ type: '1' })
    setAllSubTypes(woSubTypesResponse?.data)
  }

  const fetchDataForUrl = async (url: string) => {
    try {
      console.debug('Page <' + formTitle + '> :: called URL = ' + url + "'")
      const headerCompleted = await axiosUtils.getHeader(axiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(encodeURI(url), {
        headers: headerCompleted,
        withCredentials: false,
      })
      console.debug('data retrieved <' + formTitle + '>::<' + url + '>')
      return response
    } catch (err: any) {
      if (err.response?.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, formTitle, null, t('sessionExpired')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            formTitle,
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data?.title
                ? err.response.data.title
                : err.response.data?.message
                  ? err.response.data.message
                  : err.response.data?.error,
          ),
        )
      }
      return null
    }
  }

  const updateDependantOptions = async (
    selectedValue: string,
    resetFieldKey: 'issuer' | 'execLocation',
    setOptions: React.Dispatch<React.SetStateAction<any>>,
  ) => {
    setValue(`${resetFieldKey}.storeCode`, '')
    if (selectedValue) {
      const response = await fetchDataForUrl(
        tools.dformat(urlApis.URL_COMMON_LOCATION_LIST_STORES_FOR_COMPANY_GET, selectedValue),
      )

      const options =
        response?.data
          ?.sort((a, b) => (a.storeCode > b.storeCode ? 1 : b.storeCode > a.storeCode ? -1 : 0))
          ?.map((store: any) => ({
            value: store.storeCode,
            label: store.storeName,
          })) || []

      setOptions([DEFAULT_OPTION, ...options])
    } else {
      setOptions([DEFAULT_OPTION])
    }
  }

  const updateExecLocServiceOptions = async (companyCode: string, storeCode: string) => {
    setValue('execLocation.serviceCode', '')
    if (companyCode && storeCode) {
      const response = await fetchDataForUrl(
        tools.dformat(urlApis.URL_COMMON_LOCATION_LIST_SERVICES_FOR_COMPANY_AND_STORE_GET, companyCode, storeCode),
      )

      const services = response?.data
        ?.sort((a, b) => (a.serviceCode > b.serviceCode ? 1 : b.serviceCode > a.serviceCode ? -1 : 0))
        ?.map((service) => ({
          value: service.serviceCode,
          label: `${service.serviceCode} - ${service.serviceLib}`,
        }))
      setExecLocServiceOptions((services && [DEFAULT_OPTION, ...services]) || [DEFAULT_OPTION])
    }
  }

  const handlePnSelected = (pn: PnSearch) => {
    setModalPnOpened(false)
    methods.setValue('pn', pn?.pn)
    methods.resetField('sn')
  }

  const handleSnSelected = (sn: SnSearch) => {
    setModalSnOpened(false)
    methods.setValue('sn', sn?.sn)
  }

  const handleCancel = (isDirty: boolean) => {
    if (!isDirty) handleConfirmSubmit()
    else if (displayMode !== FORM_DISPLAY_MODUS.READ) {
      setIsConfirmOpened(true)
    }
  }

  const handleConfirmSubmit = () => {
    setIsConfirmOpened(false)
    if (displayMode === FORM_DISPLAY_MODUS.CREATE) {
      navigate(-1)
    } else {
      reset()
      setDisplayMode(DisplayModus.READ)
    }
  }

  /**
   * Badgeage de l'utilisateur connecté dans l'OE en mode Exécution
   */
  const manhourAutoScanning = async (oenumdoc: string) => {
    try {
      const headerCompleted = await axiosUtils.getHeader(axiosUtils.HEADERS.HEADER_UPDATE)

      handleLoadInProgress(true)
      const endpointUrl = encodeURI(
        tools.dformat(urlApis.URL_WORKORDER_MANHOUR_AUTO_POST_PARAMETERIZED, oenumdoc, lang),
      )
      console.debug('Component <' + formTitle + '> :: called POST::URL = ' + endpointUrl)
      const response = await axios.post(
        endpointUrl,
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )
      console.debug('data retrieved <' + formTitle + '> :: called PUT::URL = ' + endpointUrl)
      handleDisplayMessage(new Message(MessageSeverity.SUCCESS, formTitle, null, t('9510069')))
    } catch (err: any) {
      if (err.response?.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, formTitle, null, t('sessionExpired')))
      } else {
        const errorMessage = err.response?.data?.description
          ? err.response?.data?.description
          : err.response?.data?.title
            ? err.response?.data.title
            : err.response?.data?.message
              ? err.response?.data.message
              : err.response?.data?.error
                ? err.response?.data?.error
                : err.response?.data.messages[0]?.message
                  ? err.response?.data.messages[0]?.message
                  : t('error_server')
        if (errorMessage) {
          handleDisplayMessage(
            new Message(MessageSeverity.ERROR, formTitle, err.response?.data?.amasisCode, errorMessage),
          )
        } else if (err.response?.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
          handleDisplayMessage(new Message(MessageSeverity.ERROR, formTitle, null, t('9500173'), TrustedScript))
        }
      }
    } finally {
      handleLoadInProgress(false)
      onReloadManHour()
    }
  }

  const loadOptionsWithAvnumap = (avnumap: string) => {
    loadOptionsControls({}, 'code', 'label', 'label', false, false)
    loadOptionsZones({ avnumap: avnumap }, 'code', 'label', 'label', false, false)
    loadOptionsMelCodes({ avnumap: avnumap }, 'hilCode', 'hilLib', 'hilCode', false, false)
    loadOptionsMelItems(
      { avnumap: avnumap },
      'codeMelItem',
      'melLibFull',
      'codeMelItem',
      false,
      false,
      (o: MyMelItemDetail) => {
        o['codeMelItem'] = (o?.melItem as string) + o?.etops?.toString()
      },
      (o: MyMelItemDetail) => {
        o['melLibFull'] = (o?.melItem as string) + ' - ' + (o?.etops ? 'ETOPS - ' : '') + (o?.melLib as string)
      },
    )
  }

  /**
   * Fetch existing WO
   */
  useEffect(() => {
    loadData()
  }, [loadData, displayMode])

  const handleCtrl = (o) => {
    setValue('controlCode', allSubTypes.find((st) => st.code === o)?.defaultCtrlCode as string)
  }
  /**
   * Init form for new WO: load all isser and execLocation sublists
   */
  useEffect(() => {
    if (woInit) {
      loadWoIssuer()
      const avnumap = woInit.aircraft
      loadOptionsWithAvnumap(avnumap)
      if (woInit.type?.type) {
        loadOptionsSubtypes({ type: woInit.type.type }, undefined, undefined, 'code', false, false)
      }
      if (woInit.execLocation.companyCode) {
        updateDependantOptions(woInit.execLocation.companyCode, 'execLocation', setExecLocStoreOptions)
        setValue('execLocation.storeCode', woInit.execLocation.storeCode)
        if (woInit.execLocation.storeCode) {
          updateExecLocServiceOptions(woInit.execLocation.companyCode, woInit.execLocation.storeCode)
          setValue('execLocation.serviceCode', woInit.execLocation.serviceCode)
        }
      }
    }
  }, [woInit])

  /**
   * Load lists for formSelects whatever new or existing WO
   */
  useEffect(() => {
    loadSubTypes()
    loadOptionsCompanies({}, 'code', 'name', 'code', false, false)
    loadOptionsTasks({}, 'taskCode', 'taskLib', 'taskCode', false, false)
    loadOptionsWorkStation({}, 'code', 'label', 'label', false, false)
    loadOptionsPhases({}, 'code', 'label', 'label', false, false)
    loadOptionsQualifications({}, 'code', 'label', 'label', false, false)
    loadOptionsNoCompletionCodes({}, 'code', 'label', 'label', false, false)
    loadOptionsPersonnel({}, 'code', 'name', 'name', false, false)
  }, [])

  return (
    <>
      <FormProvider {...methods}>
        <form
          style={{ marginTop: '1em', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          {!creating && (
            <Row>
              <Col xxs={4}>
                <FormInput name='oenumdoc' label={t('7001905')} readOnly />
              </Col>
              <Col xxs={4}>
                <FormInput name='chantier' label={t('9500143')} readOnly />
              </Col>
              <Col xxs={4}>
                <Inline alignY='start' spacing='1-x'>
                  <FormInput name='status' label={t('9500192')} readOnly />
                  {['CLO'].includes(watch('statusAbv')) && (
                    <IconButton variant='ghost' size='xsmall'>
                      <FontAwesomeIcon icon={faLock} />
                    </IconButton>
                  )}
                </Inline>
              </Col>
            </Row>
          )}
          {!executing && (
            <>
              <Row>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormDate name='openingDate' label={t('9400064')} readOnly />
                </Col>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormControl label={t('1MOE031')} readOnly={readOnly || editing} required>
                    <Flex spacing='1-x' childrenStyle={{ flex: readOnly || editing ? 0 : 1 }}>
                      <FormSelect
                        name='issuer.companyCode'
                        rules={{ required: true }}
                        readOnly={readOnly || editing}
                        options={companiesOptions}
                        capitalize
                        error={Boolean(errors?.issuer?.companyCode)}
                        getOptionLabel={(o) => o?.value}
                        onChange={(o) => updateDependantOptions(o, 'issuer', setIssuerStoreOptions)}
                      />
                      <FormSelect
                        name='issuer.storeCode'
                        rules={{ required: true }}
                        readOnly={readOnly || editing}
                        options={issuerStoreOptions}
                        capitalize
                        error={Boolean(errors?.issuer?.storeCode)}
                        getOptionLabel={(o) => o?.value}
                      />
                    </Flex>
                  </FormControl>
                </Col>
                <Col xxs={4}>
                  {getValues('execLocation.companyCode') && !getValues('execLocation.thirdCode') && (
                    <FormControl label={t('1M00012')} readOnly={readOnly}>
                      <Flex spacing='half-x' childrenStyle={{ flex: readOnly ? 0 : 1 }}>
                        <FormSelect
                          name='execLocation.companyCode'
                          readOnly={readOnly}
                          options={companiesOptions}
                          getOptionLabel={(o) => o?.value}
                          error={Boolean(errors?.execLocation?.companyCode)}
                          onChange={(o) => {
                            updateDependantOptions(o, 'execLocation', setExecLocStoreOptions)
                            updateExecLocServiceOptions(o, selectedExecLocationStore)
                          }}
                          capitalize
                          compact
                        />
                        <FormSelect
                          name='execLocation.storeCode'
                          readOnly={readOnly}
                          options={execLocStoreOptions}
                          error={Boolean(errors?.execLocation?.storeCode)}
                          getOptionLabel={(o) => o?.value}
                          onChange={(o) => updateExecLocServiceOptions(selectedExecLocationCompany, o)}
                          capitalize
                          compact
                        />
                        <FormSelect
                          name='execLocation.serviceCode'
                          readOnly={readOnly}
                          error={Boolean(errors?.execLocation?.serviceCode)}
                          options={execLocServiceOptions}
                          getOptionLabel={(o) => o?.value}
                          capitalize
                          compact
                        />
                      </Flex>
                    </FormControl>
                  )}
                  {getValues('execLocation.thirdCode') && (
                    <FormInput name='execLocation.thirdCode' label={t('1M00012')} readOnly />
                  )}

                  {watch('hil') && (
                    <div
                      style={{ position: 'absolute', top: '29px', right: '-32px', color: 'red', margin: '0.5ex' }}
                      className='field'
                    >
                      <div>{t('9500037')}</div>
                    </div>
                  )}
                </Col>
              </Row>
              <Row>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormInput name='type.code' label={t('1P00012')} readOnly />
                </Col>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('1M00023')}
                    name='subtype.code'
                    readOnly={readOnly}
                    options={subtypesOptions}
                    error={Boolean(errors?.subtype?.code)}
                    getOptionLabel={renderOptionLabel}
                    onChange={(o) => handleCtrl(o)}
                  />
                </Col>
                <Col xxs={4}>
                  <FormControl label={t('9500042')} readOnly={!creating} required>
                    <Flex wrap={false}>
                      <FormInput name='formattedReference' readOnly style={{ flex: 2 }} />
                      {watch('reference.flightNumber') && (
                        <FormInput name='reference.flightNumber' readOnly emptyValue='' style={{ flex: 1 }} />
                      )}
                      {watch('reference.incidentNumber') && (
                        <FormInput
                          name='reference.incidentNumber'
                          rules={{ required: true }}
                          readOnly={!creating}
                          error={Boolean(errors.reference?.incidentNumber)}
                          maxLength={5}
                          capitalize
                          emptyValue=''
                          style={{ flex: 1 }}
                        />
                      )}
                    </Flex>
                  </FormControl>
                </Col>
              </Row>
              <Row>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormInput
                    name='pn'
                    label={t('9500047')}
                    labelAdornment={
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className='color_primary'
                        style={{ marginLeft: '1ex' }}
                        onClick={() => setModalPnOpened(true)}
                      />
                    }
                    readOnly={readOnly || editing}
                    maxLength={40}
                    capitalize
                    error={Boolean(errors.pn)}
                    onChange={() => methods.resetField('sn')}
                  />
                </Col>
                <Col xxs={4} style={{ paddingRight: '10px' }}>
                  <FormInput
                    name='sn'
                    label={t('9500048')}
                    labelAdornment={
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className={selectedPn ? 'color_primary' : 'color_disabled'}
                        style={{ marginLeft: '1ex' }}
                        onClick={() => (selectedPn ? setModalSnOpened(true) : null)}
                      />
                    }
                    disabled={!selectedPn}
                    readOnly={readOnly || editing}
                    maxLength={15}
                    capitalize
                    error={Boolean(errors.sn)}
                  />
                </Col>
                <Col xxs={4}>
                  <FormInput
                    label={t('0EKS004')}
                    name='ata'
                    rules={{ required: true }}
                    readOnly={readOnly || editing}
                    error={Boolean(errors?.ata)}
                    capitalize
                    maxLength={6}
                  />
                </Col>
              </Row>
              <Row>
                <Col xxs={3} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('1C00016')}
                    name='workType'
                    rules={{ required: isWorkCodeMandatory }}
                    readOnly={readOnly}
                    error={Boolean(errors.workType)}
                    options={taskOptions}
                    getOptionLabel={renderOptionLabel}
                  />
                </Col>
                <Col xxs={3} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('7000334')}
                    name='controlCode'
                    readOnly={readOnly}
                    rules={{ required: true }}
                    capitalize
                    error={Boolean(errors?.controlCode)}
                    options={controlOptions}
                    getOptionLabel={renderOptionLabel}
                  />
                </Col>
              </Row>
              <Row>
                <Col xxs={3} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('8000442')}
                    name='workStation'
                    readOnly={readOnly}
                    options={workStationOptions}
                    error={Boolean(errors.workStation)}
                    getOptionLabel={renderOptionLabel}
                    compact
                  />
                </Col>
                <Col xxs={3} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('1P08215')}
                    name='phase'
                    readOnly={readOnly}
                    error={Boolean(errors.phase)}
                    options={phaseOptions}
                    getOptionLabel={renderOptionLabel}
                    compact
                  />
                </Col>
                <Col xxs={3} style={{ paddingRight: '10px' }}>
                  <FormSelect
                    label={t('7000494')}
                    name='zone'
                    readOnly={readOnly}
                    error={Boolean(errors.zone)}
                    options={zoneOptions}
                    getOptionLabel={renderOptionLabel}
                    compact
                  />
                </Col>
                <Col xxs={3}>
                  <FormSelect
                    name='qualifications'
                    label={t('7000914') + ` (${watch('qualifications')?.length || 0}/3)`}
                    placeholder={t('7000914') as string}
                    multiple
                    options={qualificationsOptions.map((o) => ({
                      ...o,
                      disabled: (watch('qualifications')?.length > 2 && !watch('qualifications')?.includes(o)) || false,
                    }))}
                    error={Boolean(errors.qualifications)}
                    readOnly={readOnly}
                    getOptionLabel={renderOptionLabel}
                    getChipLabel={(o) => o?.value}
                    compact
                  />
                </Col>
              </Row>
              <Row>
                <WoWorkForm readOnly={readOnly} editing={editing} jobState={watch('jobState')} />
              </Row>
            </>
          )}
          {executing && (
            <Row>
              <WoExecutingForm
                jobState={watch('jobState')}
                personnelOptions={personnelOptions}
                noCompletionCodeOptions={noCompletionCodeOptions}
                melItemsOptions={melItemsOptions}
                melCodesOptions={melCodesOptions}
              />
            </Row>
          )}
          {!readOnly && (
            <Inline spacing='2-x' style={{ width: '100%', margin: '50px 0' }}>
              <Button color='secondary' variant='secondary' onClick={handleCancel}>
                {t('8000569')}
              </Button>
              <Button variant='primary' type='submit' disabled={!isDirty || isLoading}>
                {t('9500174')}
              </Button>
              {executing && (
                <Button
                  variant='warning'
                  onClick={throttle(() => manhourAutoScanning(methods.getValues('oenumdoc')), 10000, {
                    leading: true,
                    trailing: false,
                  })}
                  title={t('labor_added') as string}
                  disabled={isLoading}
                >
                  {t('7003518')}
                </Button>
              )}
            </Inline>
          )}
        </form>

        <ModalPn
          formTitle={formTitle}
          lang={lang}
          isOpened={modalPnOpened}
          avnumap={woType === '1' ? woInit?.aircraft : undefined}
          onCloseModal={() => setModalPnOpened(false)}
          onPnSelected={handlePnSelected}
        />

        <ModalSn
          formTitle={formTitle}
          isOpened={modalSnOpened}
          pn={selectedPn}
          avnumap={woType === '1' ? woInit?.aircraft : undefined}
          onCloseModal={() => setModalSnOpened(false)}
          onSnSelected={handleSnSelected}
        />
      </FormProvider>
      <DevTool control={control} />

      <ModalConfirm
        confirmMessage={t('3C00007')}
        isOpened={isConfirmOpened}
        onCloseModal={() => setIsConfirmOpened(false)}
        onSubmit={handleConfirmSubmit}
      />
    </>
  )
}

export default WoDetails
